import React from 'react';
import type { OnCloseArg } from '@withjoy/joykit';
import { Box, TextV2, LinkV2 } from '@withjoy/joykit';
import { TempSpinner } from '@shared/components/TempSpinner';
import { StylePurchaseBox, HelpText } from './ShoppingCart.styles';
import { Header, ReservedItem, PurchaseHistory, AddressCollapsible } from './components';
import type { CookedProduct, CookedProductList } from '@apps/registry/common/selectors/ProductListSelector';
import type { LineItemFragment, RegistryOrderFragment } from '@graphql/fragments/registry.graphql.generated';
import { DonationFundPlatformTypeEnum } from '@graphql/generated-base';
import { useTranslation } from '@shared/core';
import { ReactComponent as HelpIcon } from '@assets/icons/icon-help-default.svg';
import type { useRoutePaths } from '@apps/registry/guest/GuestRegistry.routes';
import { HelpDialog } from './HelpDialog';
import { usePurchaseConfirmationProvider } from '@apps/registry/guest/routes/Purchase/components/PurchaseConfirmationModal/PurchaseConfirmationModal.provider';
import { ModalStates } from '@apps/registry/guest/routes/Purchase/components/PurchaseConfirmationModal/PurchaseConfirmationModal.controller';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useCheckoutDialogContext } from '@apps/registry/guest/components/CheckoutDialog/CheckoutDialog.context';
import { useGuestRegistryState } from '@apps/registry/guest/state';
import type { ShippingAddressFragment } from '@graphql/aliases';

interface ReservedOrderProps
  extends Readonly<{
    productList: CookedProductList['products'];
    orderLineItems: Array<LineItemFragment>;
    eventId: string;
    paths: ReturnType<typeof useRoutePaths>;
    order: RegistryOrderFragment;
    openModal: (productId: string, modalStateToBeOpened?: ModalStates) => void;
  }> {}

const ReservedOrder: React.FC<ReservedOrderProps> = ({ eventId, productList, orderLineItems, paths, order, openModal }) => {
  const registryItemId = orderLineItems?.filter(lineItem => lineItem.registryItem)[0].registryItem?.id;
  const product = productList.find(product => product.id === registryItemId);

  const orderItem = orderLineItems.find(lineItem => {
    return lineItem.quantity;
  });

  if (!product) {
    return null;
  }
  return (
    <ReservedItem
      order={order}
      paths={paths}
      product={product}
      itemQuantity={orderItem ? orderItem.quantity : 1}
      eventId={eventId}
      openModal={openModal}
    />
  );
};

export type ProductList = Readonly<{
  counts: {
    everything: number;
    favorites: number;
    stillNeeded: number;
    purchased: number;
    reserved: number;
    over100: number;
    under100: number;
    available: number;
    deliverable: number;
  };
  products: readonly CookedProduct[];
}>;
interface ShoppingCartInnerProps
  extends Readonly<{
    eventId: string;
    shippingAddress: ShippingAddressFragment | null;
    isShoppingPage?: boolean;
    helpDialogOpen: boolean;
    productList: ProductList;
    reservedOrderList: RegistryOrderFragment[];
    purchasedOrderList: RegistryOrderFragment[];
    orderCount: number;
    cartOrderCount: number;
    cartTotal: string;
    paths: ReturnType<typeof useRoutePaths>;
    onClose?: (args: OnCloseArg) => void;
    showHelpDialog: () => void;
    handleDialogClose: () => void;
  }> {}

export const ShoppingCartInner: React.FC<ShoppingCartInnerProps> = ({
  eventId,
  shippingAddress,
  helpDialogOpen,
  productList,
  reservedOrderList,
  purchasedOrderList,
  orderCount,
  cartOrderCount,
  cartTotal,
  paths,
  onClose,
  showHelpDialog,
  handleDialogClose,
}) => {
  const { t } = useTranslation('sharedRegistry');
  const { openModal } = usePurchaseConfirmationProvider();
  const { openCheckoutDialog } = useCheckoutDialogContext();
  const { updateDataProvider } = useGuestRegistryState();

  const { helpText, purchaseHistoryTitle } = t('shoppingCart');
  const enableCashFundCheckoutV2 = useFeatureValue('enableCashFundCheckoutV2');
  const enableAffiliateCheckoutV2 = useFeatureValue('enableAffiliateCheckoutV2');

  return (
    <Box
      backgroundColor="mono2"
      flex={1}
      overflowY="auto"
    >
      {cartOrderCount > 0 ? (
        <>
          <Header
            giftCount={orderCount}
            cartTotal={cartTotal}
            onClose={onClose}
          />
          {reservedOrderList?.length
            ? reservedOrderList.map(order => {
                return order ? (
                  <ReservedOrder
                    paths={paths}
                    orderLineItems={order.lineItems}
                    key={order.id}
                    productList={productList.products}
                    eventId={eventId}
                    order={order}
                    openModal={(productId: string, modalStateToBeOpened?: ModalStates) => {
                      // Manual added items and Transferred registry items are not supported yet on the new flow, so here we avoid that case.
                      // `CONFIRM_PURCHASED` ("I already gave") must keep using the purchase confirmation modal. Routing it into the
                      // checkout dialog restarts the third-party payment instructions instead of confirming the existing
                      // reservation, trapping the guest in a loop. See NREG-3333.
                      if (enableCashFundCheckoutV2 && modalStateToBeOpened !== ModalStates.CONFIRM_PURCHASED) {
                        const platformType = productList.products.find(product => product.id === productId)?.donationFund?.fund.platform?.type;
                        const isDonation =
                          platformType === DonationFundPlatformTypeEnum.paypal ||
                          platformType === DonationFundPlatformTypeEnum.other ||
                          platformType === DonationFundPlatformTypeEnum.venmo ||
                          platformType === DonationFundPlatformTypeEnum.cashapp;
                        if (isDonation) {
                          openCheckoutDialog({ registryItemId: productId, currentOrderId: order.id });
                          return;
                        }
                      }

                      // Same as above: `CONFIRM_PURCHASED` must fall through to the purchase confirmation modal. The affiliate
                      // checkout branch opens the checkout dialog for any item regardless of platform, so without this guard it
                      // re-traps cash fund "I already gave" in the payment flow. See NREG-3333.
                      if (enableAffiliateCheckoutV2 && modalStateToBeOpened !== ModalStates.CONFIRM_PURCHASED) {
                        updateDataProvider({ shippingAddress, productId: productId });
                        openCheckoutDialog({ isAffiliate: true, registryItemId: productId, currentOrderId: order.id });
                        return;
                      }

                      openModal({
                        orderId: order.id,
                        productId,
                        modalStateToBeOpened,
                      });
                    }}
                  />
                ) : null;
              })
            : null}
          {purchasedOrderList.length ? (
            <StylePurchaseBox
              paddingX={5}
              paddingTop={6}
            >
              <TextV2
                typographyVariant="hed2"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {purchaseHistoryTitle()}
              </TextV2>
              {purchasedOrderList.map(order => {
                return order ? (
                  <PurchaseHistory
                    paths={paths}
                    orderId={order.id}
                    isDropship={order.isDropship}
                    orderLineItems={order.lineItems}
                    key={order.id}
                    productList={productList.products}
                    eventId={eventId}
                    note={order.note}
                    fulfillment={order.fulfillment}
                  />
                ) : null;
              })}
            </StylePurchaseBox>
          ) : null}
          <AddressCollapsible shippingAddress={shippingAddress} />
          <LinkV2
            onClick={showHelpDialog}
            textDecoration="none"
          >
            <HelpText
              typographyVariant="body1"
              display="flex"
              marginY={'64px'}
              alignItems="center"
              justifyContent="center"
            >
              <HelpIcon />
              {helpText()}
            </HelpText>
          </LinkV2>
          <HelpDialog
            isOpen={helpDialogOpen}
            onClose={handleDialogClose}
          />
        </>
      ) : (
        <TempSpinner />
      )}
    </Box>
  );
};
